import React, { useState } from "react";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard, UserPlus, CreditCard, BookOpen,
  ClipboardList, Users, DollarSign, Award, Briefcase, Library,
  Clock, FileText, MessageSquare, Bell, Calendar, BarChart2,
  Settings, LogOut, User, Menu, X, Shield, Heart, UserCog,
  TrendingUp, FolderOpen, Building, ChevronDown, ChevronRight
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

// Single unified nav — all roles see all modules (demo mode)
const ALL_NAV_ITEMS = [
  { icon: LayoutDashboard, label: "Dashboard", key: "dashboard" },
  { icon: UserPlus, label: "Admissions", key: "admissions" },
  { icon: CreditCard, label: "Fees Collection", key: "fees" },
  { icon: BookOpen, label: "LMS", key: "lms" },
  { icon: Clock, label: "Attendance", key: "attendance" },
  { icon: ClipboardList, label: "Examinations", key: "exams" },
  { icon: Award, label: "Results", key: "results" },
  { icon: Calendar, label: "Academic Planning", key: "timetable" },
  { icon: Heart, label: "Mentor-Mentee", key: "mentor" },
  { icon: Users, label: "HR Management", key: "hr" },
  { icon: DollarSign, label: "Payroll", key: "payroll" },
  { icon: Briefcase, label: "Placement", key: "placement" },
  { icon: BarChart2, label: "OBE / NAAC", key: "obe" },
  { icon: FileText, label: "Certificates", key: "certificates" },
  { icon: Library, label: "Library", key: "library" },
  { icon: Bell, label: "Communications", key: "comms" },
  { icon: MessageSquare, label: "WhatsApp", key: "whatsapp" },
  { icon: User, label: "My Profile", key: "profile" },
  { icon: Settings, label: "Settings", key: "profile" },
];

const roleAccentColors = {
  admin: "#4B7BF5",
  student: "#4B7BF5",
  faculty: "#4B7BF5",
  parent: "#4B7BF5",
  hr_manager: "#4B7BF5",
  placement_officer: "#4B7BF5",
};

const roleLabels = {
  admin: "Administrator",
  student: "Student",
  faculty: "Faculty",
  parent: "Parent",
  hr_manager: "HR Manager",
  placement_officer: "Placement Officer",
};

function SidebarContent({ user, navItems, roleLabel, activeKey, onNavClick, collapsed, mobile, onLogout }) {
  return (
    <div className="flex flex-col h-full bg-[#0F1B3D]">
      {/* Logo */}
      <div className={`flex items-center gap-3 px-5 py-5 border-b border-[#1A2850] ${collapsed && !mobile ? "justify-center" : ""}`}>
        <svg viewBox="0 0 32 32" fill="none" className="w-7 h-7">
          <rect x="1" y="21" width="8" height="8" rx="2" fill="#2A3A66"/>
          <rect x="12" y="13" width="8" height="8" rx="2" fill="#4B7BF5" opacity="0.5"/>
          <rect x="22" y="5" width="8" height="8" rx="2" fill="#4B7BF5"/>
        </svg>
        {(!collapsed || mobile) && (
          <span className="font-['DM_Sans'] font-medium text-[#EEEADF] tracking-[0.08em]">acadence</span>
        )}
      </div>

      {/* Role badge */}
      {(!collapsed || mobile) && (
        <div className="px-5 py-3 border-b border-[#1A2850]">
          <span className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#4B7BF5]">{roleLabel}</span>
        </div>
      )}

      {/* Nav items */}
      <nav className="flex-1 py-3 px-2 overflow-y-auto sidebar-scroll space-y-0.5">
        {navItems.map(item => {
          const isActive = activeKey === item.key;
          return (
            <button
              key={item.key}
              data-testid={`nav-${item.key}`}
              onClick={() => onNavClick(item.key)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm transition-all ${
                collapsed && !mobile ? "justify-center" : ""
              } ${
                isActive
                  ? "bg-[#4B7BF5] text-white"
                  : "text-[#EEEADF]/40 hover:text-[#EEEADF] hover:bg-[#EEEADF]/5"
              }`}
            >
              <item.icon className={`w-4 h-4 flex-shrink-0 ${isActive ? "text-white" : ""}`} strokeWidth={isActive ? 2 : 1.5} />
              {(!collapsed || mobile) && (
                <span className={`font-['Syne'] font-${isActive ? "bold" : "medium"} text-xs uppercase tracking-wider`}>
                  {item.label}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* User */}
      <div className={`border-t border-[#1A2850] p-4 ${collapsed && !mobile ? "flex justify-center" : ""}`}>
        {collapsed && !mobile ? (
          <button
            onClick={onLogout}
            data-testid="logout-collapsed-btn"
            className="p-2 text-[#EEEADF]/30 hover:text-[#FF3636] transition-colors"
          >
            <LogOut className="w-4 h-4" />
          </button>
        ) : (
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-[#4B7BF5] flex items-center justify-center flex-shrink-0">
              <span className="font-['Syne'] font-bold text-white text-sm">
                {user?.name?.charAt(0)?.toUpperCase() || "U"}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-['Syne'] font-bold text-xs text-[#EEEADF] truncate">{user?.name}</p>
              <p className="font-['DM_Sans'] text-[10px] text-[#EEEADF]/30 truncate">{user?.email}</p>
            </div>
            <button
              onClick={onLogout}
              data-testid="logout-btn"
              className="p-1.5 text-[#EEEADF]/30 hover:text-[#FF3636] transition-colors flex-shrink-0"
            >
              <LogOut className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const navItems = ALL_NAV_ITEMS;
  const roleLabel = roleLabels[user?.role] || "User";
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const getActiveKey = () => {
    const path = location.pathname.split("/dashboard/")[1] || "dashboard";
    return path || "dashboard";
  };
  const activeKey = getActiveKey();

  return (
    <div className="flex h-screen overflow-hidden bg-[#F5F2EA]">
      {/* Desktop Sidebar */}
      <motion.aside
        animate={{ width: collapsed ? 64 : 240 }}
        transition={{ duration: 0.2, ease: "easeInOut" }}
        className="hidden md:block flex-shrink-0 relative"
        data-testid="sidebar"
      >
        <SidebarContent
          user={user} navItems={navItems} roleLabel={roleLabel}
          activeKey={activeKey}
          onNavClick={(key) => navigate(key === "dashboard" ? "/dashboard" : `/dashboard/${key}`)}
          collapsed={collapsed} mobile={false} onLogout={handleLogout}
        />
        {/* Collapse toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          data-testid="sidebar-collapse-btn"
          className="absolute -right-3 top-[72px] w-6 h-6 bg-[#0F1B3D] border border-[#1A2850] flex items-center justify-center hover:bg-[#1A2850] transition-colors z-10"
        >
          <ChevronRight className={`w-3 h-3 text-[#4B7BF5] transition-transform ${collapsed ? "" : "rotate-180"}`} />
        </button>
      </motion.aside>

      {/* Mobile overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setMobileOpen(false)}
              className="md:hidden fixed inset-0 bg-black/50 z-40"
            />
            <motion.aside
              initial={{ x: -260 }} animate={{ x: 0 }} exit={{ x: -260 }}
              transition={{ duration: 0.2 }}
              className="md:hidden fixed left-0 top-0 bottom-0 w-64 z-50"
            >
              <SidebarContent
                user={user} navItems={navItems} roleLabel={roleLabel}
                activeKey={activeKey}
                onNavClick={k => { navigate(k === "dashboard" ? "/dashboard" : `/dashboard/${k}`); setMobileOpen(false); }}
                collapsed={false} mobile={true} onLogout={handleLogout}
              />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top nav */}
        <header
          className="h-14 bg-[#F5F2EA] border-b border-[#D8D4CB] flex items-center justify-between px-6 flex-shrink-0"
          data-testid="top-nav"
        >
          <div className="flex items-center gap-4">
            <button
              onClick={() => setMobileOpen(true)}
              className="md:hidden p-1.5 text-[#7A7468] hover:text-[#0F1B3D]"
              data-testid="mobile-menu-open-btn"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div>
              <h1 className="font-['Syne'] font-bold text-sm uppercase tracking-wider text-[#0F1B3D]">
                {navItems.find(n => n.key === activeKey)?.label || "Dashboard"}
              </h1>
              <p className="font-['DM_Sans'] text-[10px] text-[#BDBAA8] hidden sm:block">
                {new Date().toLocaleDateString("en-IN", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button className="relative p-2 text-[#7A7468] hover:text-[#0F1B3D] transition-colors" data-testid="notifications-btn">
              <Bell className="w-4 h-4" strokeWidth={1.5} />
              <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 bg-[#FF3636]" />
            </button>

            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                data-testid="user-menu-btn"
                className="flex items-center gap-2 hover:bg-[#E8E4DC] px-2 py-1.5 transition-colors"
              >
                <div className="w-7 h-7 bg-[#0F1B3D] flex items-center justify-center flex-shrink-0">
                  <span className="font-['Syne'] font-bold text-[#4B7BF5] text-xs">
                    {user?.name?.charAt(0)?.toUpperCase() || "U"}
                  </span>
                </div>
                <span className="font-['Syne'] font-bold text-xs text-[#0F1B3D] hidden sm:block">
                  {user?.name?.split(" ")[0]}
                </span>
                <ChevronDown className="w-3.5 h-3.5 text-[#7A7468] hidden sm:block" />
              </button>

              <AnimatePresence>
                {userMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 6 }}
                    className="absolute right-0 top-11 w-52 bg-white border border-[#D8D4CB] z-50 shadow-lg shadow-black/5"
                  >
                    <div className="px-4 py-3 border-b border-[#E8E4DC]">
                      <p className="font-['Syne'] font-bold text-xs text-[#0F1B3D]">{user?.name}</p>
                      <p className="font-['DM_Sans'] text-[10px] text-[#7A7468]">{roleLabel}</p>
                    </div>
                    <div className="p-1.5">
                      <button onClick={() => { navigate("/dashboard/profile"); setUserMenuOpen(false); }} className="w-full flex items-center gap-2 px-3 py-2 text-xs font-['Syne'] font-medium text-[#0F1B3D] hover:bg-[#F5F2EA] transition-colors">
                        <User className="w-3.5 h-3.5" /> Profile
                      </button>
                      <button onClick={() => { navigate("/dashboard/profile"); setUserMenuOpen(false); }} className="w-full flex items-center gap-2 px-3 py-2 text-xs font-['Syne'] font-medium text-[#0F1B3D] hover:bg-[#F5F2EA] transition-colors">
                        <Settings className="w-3.5 h-3.5" /> Settings
                      </button>
                      <div className="border-t border-[#E8E4DC] my-1" />
                      <button
                        onClick={handleLogout}
                        data-testid="user-menu-logout-btn"
                        className="w-full flex items-center gap-2 px-3 py-2 text-xs font-['Syne'] font-medium text-[#FF3636] hover:bg-[#FF3636]/5 transition-colors"
                      >
                        <LogOut className="w-3.5 h-3.5" /> Sign out
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-6 lg:p-8" data-testid="dashboard-content">
          <motion.div
            key={activeKey}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            <Outlet />
          </motion.div>
        </main>
      </div>
    </div>
  );
}
